import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";
import { fetcher } from "@/lib/hooks";
import { useAuth } from "@/lib/context";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";

export function Stars({ value, onChange, size = 16, testId = "stars" }) {
  return (
    <div className="flex gap-0.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={`${testId}-${n}`} aria-label={`${n}/5`}
          className={`transition-transform ${onChange ? "hover:scale-110" : "cursor-default"}`}>
          <Star size={size} className={n <= value ? "fill-[#B8860B] text-[#B8860B]" : "text-slate-300"} />
        </button>
      ))}
    </div>
  );
}

export function ReviewForm({ token, onDone }) {
  const { t } = usePrefs();
  const [f, setF] = useState({ rating: 0, comment: "", name: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/public/track/${token}/review`, f); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="review-form">
      <div className="font-display text-xl font-bold">{t("rateTrip")}</div>
      <Stars value={f.rating} onChange={(rating) => setF({ ...f, rating })} size={32} testId="review-stars" />
      <Input placeholder={t("yourName")} value={f.name} maxLength={80} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="review-name-input" />
      <Textarea placeholder={t("yourComment")} value={f.comment} maxLength={1000} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="review-comment-input" />
      <Button onClick={send} disabled={!f.rating || busy} className="w-full rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="review-submit-button">{t("sendReview")}</Button>
    </div>
  );
}

function FollowUp({ r }) {
  const { t, fdatetime } = usePrefs();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const fu = r.follow_up;
  if (fu?.handled) return (
    <div className="mt-1.5 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid={`review-followup-${r.id}`}>
      <div className="flex items-center gap-1 font-bold"><CheckCircle2 size={12} />{t("handledBy")} {fu.by_name} · {fdatetime(fu.at)}</div>
      <p className="mt-0.5">{fu.note}</p>
    </div>
  );
  if (!["admin", "supervision", "gestionnaire"].includes(user?.role)) return null;
  const save = async () => {
    try { await api.post(`/reviews/${r.id}/follow-up`, { note }); qc.invalidateQueries({ queryKey: ["reviews"] }); qc.invalidateQueries({ queryKey: ["ticket", r.ticket_id] }); qc.invalidateQueries({ queryKey: ["ticket"] }); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return open ? (
    <div className="mt-1.5 space-y-2" data-testid={`review-followup-form-${r.id}`}>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder={t("followUpNote")} className="bg-white text-sm" data-testid={`review-followup-note-${r.id}`} />
      <div className="flex gap-2">
        <Button size="sm" disabled={note.trim().length < 3} onClick={save} className="h-7 rounded-sm bg-emerald-700 text-xs hover:bg-emerald-800" data-testid={`review-followup-save-${r.id}`}>{t("markHandled")}</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-7 text-xs">{t("cancel")}</Button>
      </div>
    </div>
  ) : <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="mt-1.5 h-7 rounded-sm border-rose-300 text-xs text-rose-800" data-testid={`review-followup-open-${r.id}`}>{t("markHandled")}</Button>;
}

export function ReviewCard({ r, showTicket = true }) {
  const { t, fdatetime } = usePrefs();
  return (
    <div className={`space-y-1 px-5 py-3 ${r.rating <= 2 ? "border-s-4 border-rose-600 bg-rose-50" : ""}`} data-testid={`review-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Stars value={r.rating} size={13} testId={`review-rating-${r.id}`} />
        <span className="flex items-center gap-2 text-[11px] text-slate-400">{r.rating <= 2 && !r.follow_up?.handled && <span className="rounded-sm bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`review-low-${r.id}`}>{t("lowAlert")}</span>}{fdatetime(r.created_at)}</span>
      </div>
      {r.comment && <p className="text-sm text-slate-700">« {r.comment} »</p>}
      <div className="text-[11px] text-slate-500">
        {r.guest_name || t("guest")}{r.provider_name ? ` · ${r.provider_name}` : ""}
        {showTicket && r.ticket_id && <> · <Link to={`/tickets/${r.ticket_id}`} className="font-data text-[#996515] hover:underline">{r.number}</Link></>}
      </div>
      {r.rating <= 2 && <FollowUp r={r} />}
    </div>
  );
}

export function ReviewsPanel() {
  const { t } = usePrefs();
  const [onlyOpen, setOnlyOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["reviews"], queryFn: () => fetcher("/reviews") });
  if (!data) return null;
  const items = onlyOpen ? data.items.filter((r) => r.rating <= 2 && !r.follow_up?.handled) : data.items;
  return (
    <div className="mb-6 rounded-sm border border-slate-200 bg-white" data-testid="reviews-panel">
      <div className="flex items-center justify-between border-b px-5 py-3">
        <span className="flex items-center gap-3">
          <span className="font-display text-lg font-bold">{t("reviews")}</span>
          {(data.open_low > 0 || onlyOpen) && <button onClick={() => setOnlyOpen(!onlyOpen)} data-testid="reviews-open-low-filter"
            className={`rounded-sm px-2 py-0.5 text-[11px] font-bold ${onlyOpen ? "bg-rose-600 text-white" : "bg-rose-100 text-rose-800"}`}>{data.open_low} {t("openLow")}</button>}
        </span>
        {data.average != null && <span className="flex items-center gap-2 text-sm" data-testid="reviews-average"><span className="text-slate-500">{t("averageRating")}</span><Stars value={Math.round(data.average)} size={14} testId="reviews-average-stars" /><span className="font-data font-bold">{data.average.toFixed(1)}</span><span className="text-slate-400">({data.count})</span></span>}
      </div>
      {items.length === 0 ? <p className="p-5 text-sm text-slate-500" data-testid="reviews-empty">{onlyOpen ? t("noOpenLow") : t("noReviews")}</p>
        : <div className="max-h-96 divide-y divide-slate-100 overflow-y-auto">{items.map((r) => <ReviewCard key={r.id} r={r} />)}</div>}
    </div>
  );
}
